import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { ReactSVG } from "react-svg";
import { classNames, useTranslate } from "helpers";
import { CheckBox } from "ui/components/CheckBox";
import { ColumnState } from "./useColumnVisibility";

export interface ColumnToggleProps {
  columnStates: ColumnState[];
  onToggle: (key: string) => void;
}

const ColumnToggle: React.FC<ColumnToggleProps> = ({
  columnStates,
  onToggle,
}) => {

  const { t } = useTranslate();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    },
    []
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, handleClickOutside]);

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={classNames(
          "flex items-center justify-center w-8 h-8 rounded-sm transition-colors",
          open
            ? "bg-blue-100 text-blue-600"
            : "bg-slate-100 text-slate-600 hover:bg-blue-100 hover:text-blue-600"
        )}
      >
        <ReactSVG
          src="/media/icons/list-check.svg"
          className="w-4 h-4"
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-[200px] py-2">
          {columnStates.map((col) => (
            <div
              key={col.key}
              className={classNames(
                "px-3 py-1.5 hover:bg-gray-50",
                col.alwaysVisible ? "opacity-50" : ""
              )}
            >
              <CheckBox
                checked={col.visible}
                disabled={col.alwaysVisible}
                onChange={() => onToggle(col.key)}
                label={t(col.title)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(ColumnToggle);
